/** CrumblePlatform (+Generator), TouchBlock, TickTockPlatformHandler, DashPlatform, ColorMatchBlock. */
import { GameBehaviour } from '../game.ts';
import { type Collision2D, type SimCollider, type SimGo } from '../world.ts';
import { FrameCounter, moveTowards, vangle, type RGBA } from '../unity.ts';
import { bool, num, rgba } from '../props.ts';
import type { SpriteRef } from '../../../data/gameData.ts';
import { ActivatorBase } from './activators.ts';
import { DynamicObject } from './dynamic.ts';
import { PlayerController } from '../player.ts';
import { activeCollidersIn, blocksRespawn, playerOf, spriteRef } from './util.ts';
import { Animator } from './animator.ts';

const lerpColor = (a: RGBA, b: RGBA, t: number): RGBA => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];

// ---------- CrumblePlatform ----------

const enum CrumbleState {
  Solid,
  Crumbling,
  Gone,
}

export class CrumblePlatform extends GameBehaviour {
  colissionGoneTime = FrameCounter.seconds(0.4);
  animationLength = FrameCounter.seconds(10 / 12);
  respawnCounter = FrameCounter.seconds(5);
  fadeInCounter = FrameCounter.seconds(0.3);
  state = CrumbleState.Solid;
  col: SimCollider | null = null;
  animator: Animator | null = null;
  override noSave = ['col', 'animator'];

  override awake() {
    this.col = this.go.colliders[0] ?? null;
  }

  override start() {
    this.animator = this.go.getBehaviour(Animator);
    this.freezeAnimation();
  }

  /** animator.enabled = false; speed = 0; Play(0, 0, 0); Update(0) */
  private freezeAnimation() {
    const a = this.animator;
    if (!a) return;
    a.enabled = false;
    a.speed = 0;
    a.playDefault(0);
  }

  private startCrumble() {
    if (this.state !== CrumbleState.Solid) return;
    this.state = CrumbleState.Crumbling;
    this.sound('crumble');
    this.colissionGoneTime.start();
    this.animationLength.start();
    const a = this.animator;
    if (a) {
      a.enabled = true;
      a.speed = 1;
      a.playDefault(0);
    }
  }

  override fixedUpdate() {
    const sprite = this.go.sprite;
    switch (this.state) {
      case CrumbleState.Crumbling:
        if (this.colissionGoneTime.countCheckFinishReset()) this.col?.setTrigger(true);
        if (this.animationLength.countCheckFinishReset()) {
          if (sprite) sprite.enabled = false;
          this.state = CrumbleState.Gone;
          this.sound('crumbleBreak');
          this.respawnCounter.start();
          this.fadeInCounter.reset();
        }
        break;
      case CrumbleState.Gone:
        this.respawnCounter.count();
        if (this.respawnCounter.isFinished() && this.canRespawn()) {
          this.respawnCounter.restart();
          this.fadeInCounter.start();
          this.state = CrumbleState.Solid;
          this.sound('platformRespawn');
          this.col?.setTrigger(false);
          if (sprite) sprite.enabled = true;
          this.freezeAnimation();
        }
        break;
    }
    if (this.fadeInCounter.isRunningNotFinished() && sprite) {
      this.fadeInCounter.count();
      let a: number;
      if (this.fadeInCounter.isFinished()) {
        a = 1;
        this.fadeInCounter.reset();
      } else a = this.fadeInCounter.percentage;
      sprite.color = [sprite.color[0], sprite.color[1], sprite.color[2], a];
    }
  }

  private canRespawn(): boolean {
    if (!this.col) return true;
    const own = this.col.attachedRigidbody;
    return !this.world.overlapCollider(this.col).some((c) => c.attachedRigidbody !== own && blocksRespawn(c, true));
  }

  override onCollisionEnter(c: Collision2D) {
    const body = c.gameObject.body;
    if (!body?.isDynamic) return;
    if (c.contacts.some((cp) => vangle({ x: 0, y: -1 }, cp.normal) < 55)) this.startCrumble();
  }
}

export class CrumblePlatformGenerator extends GameBehaviour {
  static override checkpointable = false;
  override start() {
    // Regenerating in the Unity editor removes the prefab's own tiles (m_RemovedGameObjects), which the
    // level format does not record: when generated tiles were added to the instance, drop the prefab ones.
    const prefabKey = `${this.go.key}/`;
    const kids = this.go.children.filter((c) => c.getBehaviour(CrumblePlatform));
    const added = kids.filter((c) => !c.key.startsWith(prefabKey));
    if (added.length) for (const c of kids) if (c.key.startsWith(prefabKey)) c.destroy();
    const sr = this.go.sprite;
    if (sr) {
      for (const c of this.go.children) if (!c.destroyed && c.sprite) c.sprite.color = [...sr.color] as RGBA;
      sr.enabled = false;
    }
  }
}

// ---------- TouchBlock ----------

export class TouchBlock extends ActivatorBase {
  fadeFromColor: RGBA = [1, 1, 1, 1];
  fadeToColor: RGBA = [0, 0, 0, 0];
  fadeCounter = FrameCounter.seconds(0.5);
  colorSprite: SimGo | null = null;
  override noSave = ['colorSprite'];

  override awake() {
    this.fadeToColor = rgba(this.data.fadeToColor);
  }

  override start() {
    this.colorSprite = this.go.descendants().find((g) => g.active && g.sprite && g.name === 'Color') ?? null;
  }

  override fixedUpdate() {
    if (this.fadeCounter.isRunningNotFinished()) {
      this.fadeCounter.count();
      if (this.colorSprite) this.colorSprite.sprite!.color = lerpColor(this.fadeFromColor, this.fadeToColor, this.fadeCounter.range(0, 1));
    }
    const rb = this.go.body;
    if (!rb) return;
    for (const cp of rb.getContacts()) {
      const p = cp.rigidbody ? playerOf(cp.rigidbody.go) : null;
      if (p) this.turnOn(p.go.sprite?.color ?? p.color);
    }
  }

  private turnOn(color: RGBA) {
    const t = this.fadeToColor;
    if (!this.isActivated || t[0] !== color[0] || t[1] !== color[1] || t[2] !== color[2]) this.sound('touchBlock');
    this.activate();
    if (this.colorSprite) this.fadeFromColor = [...this.colorSprite.sprite!.color] as RGBA;
    this.fadeToColor = [...color] as RGBA;
    this.fadeCounter.restart();
  }
}

// ---------- TickTockPlatformHandler ----------

export class TickTockPlatformHandler extends GameBehaviour {
  platformSprite: SpriteRef | null = null;
  outlineSprite: SpriteRef | null = null;
  invert = false;
  activator: ActivatorBase | null = null;
  platformCollider: SimCollider | null = null;
  isSolid = true;
  colliderBounds: [number, number, number, number] | null = null;
  override noSave = ['platformSprite', 'outlineSprite', 'activator', 'platformCollider', 'colliderBounds'];

  override awake() {
    this.platformSprite = spriteRef(this.game.gd, this.data.platformSprite);
    this.outlineSprite = spriteRef(this.game.gd, this.data.outlineSprite);
    this.invert = bool(this.data.invert);
  }

  override start() {
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
    this.platformCollider = this.go.colliders[0] ?? null;
    this.colliderBounds = this.platformCollider?.bounds() ?? null;
    this.updateState(true);
  }

  override fixedUpdate() {
    this.updateState();
  }

  private shouldBeSolid() {
    return this.activator!.isActivated !== this.invert;
  }

  private updateState(quiet = false) {
    // a missing activator throws in Unity before anything changes
    if (!this.activator) return;
    const want = this.shouldBeSolid();
    if (want === this.isSolid) return;
    if (!quiet) this.sound('tickTock');
    if (want) {
      const b = this.colliderBounds;
      const hits = b ? this.world.overlapBox({ x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2 }, { x: b[2] - b[0], y: b[3] - b[1] }) : [];
      if (!hits.some((c) => blocksRespawn(c, false))) this.changeState(true);
    } else this.changeState(false);
  }

  private changeState(solid: boolean) {
    const sr = this.go.sprite;
    if (this.platformCollider) this.platformCollider.enabled = solid;
    this.isSolid = solid;
    if (!sr) return;
    const s = solid ? this.platformSprite : this.outlineSprite;
    if (s) sr.sprite = s;
    sr.sortingLayer = solid ? 'Custom Blocks in Front of Spikes and Level (default custom blocks)' : 'Background';
    sr.order = solid ? 0 : 1;
  }
}

// ---------- DashPlatform ----------

export class DashPlatform extends ActivatorBase {
  wasDashedThrough = false;
  fadeInRate = 2;
  fadeOutRate = 3;
  targetAlpha = 0;
  currentAlpha = 0;
  staySolidCounter = new FrameCounter();
  triggerCollider: SimCollider | null = null;
  platformCollider: SimCollider | null = null;
  override noSave = ['triggerCollider', 'platformCollider'];

  override awake() {
    this.staySolidCounter = FrameCounter.from(this.data.staySolidCounter, 0);
  }

  override start() {
    this.wasDashedThrough = false;
    const cols = activeCollidersIn(this.go);
    if (cols.length < 2) return;
    if (cols[0].isTrigger) [this.triggerCollider, this.platformCollider] = [cols[0], cols[1]];
    else [this.triggerCollider, this.platformCollider] = [cols[1], cols[0]];
  }

  private get platformSprite() {
    return this.platformCollider?.go.sprite ?? null;
  }

  private isValidCollider(c: SimCollider): boolean {
    return c.go !== this.go && blocksRespawn(c, false);
  }

  private makeSolid() {
    this.wasDashedThrough = false;
    if (this.platformCollider) this.platformCollider.enabled = true;
    this.targetAlpha = 1;
    this.staySolidCounter.start();
    this.activate();
    this.sound('dashPlatform');
  }

  private makeTransparent() {
    if (this.platformCollider) this.platformCollider.enabled = false;
    this.targetAlpha = 0;
    this.deactivate();
    this.sound('dashPlatform');
  }

  override update(dt: number) {
    const sr = this.platformSprite;
    if (!sr) return;
    this.currentAlpha = moveTowards(this.currentAlpha, this.targetAlpha, dt * (this.wasDashedThrough ? this.fadeInRate : this.fadeOutRate));
    sr.color = [sr.color[0], sr.color[1], sr.color[2], this.currentAlpha];
  }

  override fixedUpdate() {
    if (!this.triggerCollider || !this.platformCollider) return;
    if (!this.isActivated) {
      let valid = 0;
      for (const h of this.world.overlapCollider(this.triggerCollider)) {
        const p = playerOf(h);
        if (p?.isDashing()) this.wasDashedThrough = true;
        if (this.isValidCollider(h)) valid++;
      }
      if (this.wasDashedThrough && valid === 0 && !this.platformCollider.enabled) this.makeSolid();
    }
    if (this.staySolidCounter.countCheckFinishReset()) this.makeTransparent();
  }
}

// ---------- ColorMatchBlock ----------

export class ColorMatchBlock extends ActivatorBase {
  passThroughAlpha = 0.3;
  solidAlpha = 1;
  backToSolidCounter = new FrameCounter(5);
  playerIndex = 0;

  override awake() {
    this.backToSolidCounter = FrameCounter.from(this.data.backToSolidCounter, 5 / 120);
    this.playerIndex = num(this.data.playerIndex);
  }

  /** player colour last applied to the sprite; the authored tint stays until that player changes colour */
  private seenColor: [number, number, number] | null = null;

  override start() {
    this.setActiveState(true);
  }

  private get collider() {
    return this.go.colliders[0] ?? null;
  }

  private ownColliders() {
    const rb = this.go.body;
    return this.go.collidersInChildren().filter((c) => c.attachedRigidbody === rb);
  }

  private setAlpha(a: number) {
    const sr = this.go.sprite;
    if (sr) sr.color = [sr.color[0], sr.color[1], sr.color[2], a];
  }

  /** ColourMatchBlock.OnUIActionPerformed: this player's blocks take their new colour, keeping alpha. */
  private syncPlayerColor() {
    const player = this.game.players[this.playerIndex];
    const sr = this.go.sprite;
    if (!(player instanceof PlayerController) || !sr) return;
    const c = player.color;
    if (!this.seenColor) {
      this.seenColor = [c[0], c[1], c[2]];
      return;
    }
    if (this.seenColor[0] === c[0] && this.seenColor[1] === c[1] && this.seenColor[2] === c[2]) return;
    this.seenColor = [c[0], c[1], c[2]];
    sr.color = [c[0], c[1], c[2], sr.color[3]];
  }

  private makeSolid() {
    this.collider?.setTrigger(false);
    this.setAlpha(this.solidAlpha);
    this.backToSolidCounter.reset();
    this.activate();
    this.sound('colorBlock');
  }

  private makeTransparent() {
    this.collider?.setTrigger(true);
    this.setAlpha(this.passThroughAlpha);
    this.backToSolidCounter.start();
    this.deactivate();
    this.sound('colorBlock');
  }

  /**
   * Kinematic, without full kinematic contacts: only dynamic bodies are seen.
   * The matching player stands on it. Anyone else, or a dynamic prop, turns it into a
   * trigger until they have left for a few frames. Their velocity is put back so the
   * hit does not bounce them, unless they are dashing.
   */
  override fixedUpdate() {
    this.syncPlayerColor();
    const rb = this.go.body;
    if (!rb) return;
    if (this.isActivated) {
      let invalid = false;
      for (const cp of rb.getContacts()) {
        const hit = cp.rigidbody;
        if (!hit?.isDynamic) continue;
        const player = playerOf(hit.go);
        if (player) {
          if (player.index !== this.playerIndex) {
            if (!player.isDashing()) player.body.velocity = { ...player.lastPlayerVelocity };
            invalid = true;
          }
        } else {
          const dyn = hit.go.getBehaviour(DynamicObject);
          if (dyn) hit.velocity = { ...dyn.lastDynamicVelocity };
          invalid = true;
        }
      }
      if (invalid) this.makeTransparent();
    } else {
      const hits = this.ownColliders().flatMap((c) => this.world.overlapCollider(c));
      if (hits.some((c) => c.attachedRigidbody?.isDynamic)) this.backToSolidCounter.restart();
      else if (this.backToSolidCounter.countCheckFinishReset()) this.makeSolid();
    }
  }
}
